import React, { FC } from 'react';
import clsx from 'clsx';
import withStyles from '@mui/styles/withStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Checkbox, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import { grey } from '@mui/material/colors';
import { frequencies } from '../constants/frequencies';

import { SettingsData } from './Settings';

const hideFrequencyColumn = true;

const StyledTableCell = withStyles(theme => ({
  root: {
    borderBottomStyle: 'dashed',
  },
  head: {
    color: theme.palette.primary.main,
  },
}))(TableCell);
const useTableCellEmailSectionStyles = makeStyles({
  state: {
    borderLeft: `1px solid ${grey[400]}`,
  },
});
const useButtonStyles = makeStyles(theme => ({
  root: {
    borderColor: grey[300],
    color: grey[400],
    '&:hover': {
      color: theme.palette.primary.main,
      borderColor: grey[300],
    },
    '&$selected': {
      color: theme.palette.primary.main,
    },
  },
  selected: {},
}));
const keys = {
  isPush: 'is_push',
  isEmail: 'is_email',
  frequency: 'frequency',
};

type SettingsTabProps = {
  data: Array<SettingsData>;
  onChange: (id: string, field: string, value: any) => void;
};

const SettingsTab: FC<SettingsTabProps> = ({ data, onChange }) => {
  const { t } = useTranslation();
  const buttonClasses = useButtonStyles();
  const muiButtonClasses = { root: buttonClasses.root };
  const tableCellEmailSectionClasses = useTableCellEmailSectionStyles();

  const StyledButton = (props: any) => <Button classes={muiButtonClasses} {...props} />;

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <StyledTableCell width="45%">{t('Notification name')}</StyledTableCell>
            <StyledTableCell width="15%" align="center">
              {t('Push')}
            </StyledTableCell>
            <StyledTableCell width="15%" align="center">
              {t('Email')}
            </StyledTableCell>
            {hideFrequencyColumn ? null : (
              <StyledTableCell width="25%" align="center">
                {t('Frequency')}
              </StyledTableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map(({ name, description, isPush, isEmail, frequency, id }) => (
            <TableRow key={name}>
              <StyledTableCell>
                <Tooltip title={description} placement="top" arrow>
                  <span>{name}</span>
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell align="center">
                <Checkbox
                  checked={isPush}
                  onChange={(event, value) => onChange(id, keys.isPush, value)}
                />
              </StyledTableCell>
              <StyledTableCell align="center" className={tableCellEmailSectionClasses.state}>
                <Checkbox
                  checked={isEmail}
                  onChange={(event, value) => onChange(id, keys.isEmail, value)}
                />
              </StyledTableCell>

              {hideFrequencyColumn ? null : (
                <StyledTableCell align="center">
                  <ButtonGroup color="primary" disabled={!isEmail}>
                    {Object.values(frequencies).map(({ id: frequencyId, label }) => {
                      const isCurrent = frequencyId === frequency;

                      return (
                        <StyledButton
                          key={frequencyId}
                          onClick={() => onChange(id, keys.frequency, frequencyId)}
                          disabled={isCurrent}
                          className={clsx({ [buttonClasses.selected]: isCurrent && isEmail })}
                        >
                          {label}
                        </StyledButton>
                      );
                    })}
                  </ButtonGroup>
                </StyledTableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export { SettingsTab };
